import React from 'react'
import PropTypes from 'prop-types'
import { Button, Typography } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { BATCH_WARNING_KIND } from './batchWarning'

/**
 * Historia 120 — Bloque 12 (Jupiter, paquete P6), `features/S129-avisos-grupo-lote-y-cierre-jupiter.feature`.
 *
 * ONE component, THREE variants selected by `kind` (one of `BATCH_WARNING_KIND`) -- not three
 * dialogs, exactly the same discipline `batchReasons.jsx`/`BatchBlockedNotice.jsx` already apply to
 * their own "one renderer" rule. Every variant offers exactly TWO choices and nothing is executed
 * until the operator picks one (S129/S134/S136): dismissing the dialog (`onClose`) fires neither.
 *
 * `GROUP_SHORTCUT` is the only variant that needs `planningGroup` (S129's "con el nombre del grupo").
 * The frozen catalogue key carries a literal `{group}` placeholder that i18next's default `{{ }}`
 * interpolation does NOT touch (`src/i18n.js` never changed the prefix/suffix), so it is substituted
 * here with a plain `.replace` -- the same technique this app already uses elsewhere for a
 * single-brace contract literal (`DUPLICATE_PLANNING_GROUP_MESSAGE_KEY`, `.replace('{{values}}', …)`
 * in `configuration/workflow2.0/planningGroupSaveError.js`, just with the single-brace flavour this
 * particular key was frozen with in `03-dominio.md` §7.3).
 */
const VARIANTS = {
  [BATCH_WARNING_KIND.GROUP_SHORTCUT]: {
    message: (t_, planningGroup) => t_('This job belongs to the planning group {group}').replace('{group}', planningGroup || ''),
    batchLabel: 'Start the whole group',
    individualLabel: 'Start only this job'
  },
  [BATCH_WARNING_KIND.RESUME_BATCH]: {
    message: (t_) => t_('This job was paused together with other jobs'),
    batchLabel: 'Resume the whole batch',
    individualLabel: 'Resume only this job'
  },
  [BATCH_WARNING_KIND.FINISH_BATCH_MEMBER]: {
    message: (t_) => t_('This job is running together with other jobs'),
    batchLabel: 'Finish the whole batch',
    individualLabel: 'Finish only this job'
  }
}

const EMPTY_VARIANT = { message: () => '', batchLabel: '', individualLabel: '' }

const BatchWarningDialog = ({
  open = false,
  kind = BATCH_WARNING_KIND.NONE,
  planningGroup = '',
  onClose = () => {},
  onChooseBatch = () => {},
  onChooseIndividual = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const variant = VARIANTS[kind] || EMPTY_VARIANT

  const renderCustomButtons = (
    <>
      <Button variant='outlined' onClick={onChooseIndividual}>{t_(variant.individualLabel)}</Button>
      <Button variant='contained' color='warning' onClick={onChooseBatch}>{t_(variant.batchLabel)}</Button>
    </>
  )

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'warning', title: t_('Warning'), icon: WarningAmberRoundedIcon })}
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      renderCustomButtons={renderCustomButtons}
    >
      <Typography>{variant.message(t_, planningGroup)}</Typography>
    </MosSmartDialog>
  )
}

BatchWarningDialog.propTypes = {
  open: PropTypes.bool,
  kind: PropTypes.oneOf(Object.values(BATCH_WARNING_KIND)),
  planningGroup: PropTypes.string,
  onClose: PropTypes.func,
  onChooseBatch: PropTypes.func,
  onChooseIndividual: PropTypes.func
}

export default BatchWarningDialog
